import {AnyAction, countPatchChanges, IDocumentData, ImmerPatchSequence} from "@buildwithflux/core";
import type {FluxServices} from "export";
import {EMPTY, Subject} from "rxjs";
import {catchError, filter, map, takeUntil, tap} from "rxjs/operators";

import type {AppStore} from "modules/app/state/app";
import {snackbarErrorGeneratingHelper} from "modules/common/providers/FluxSnackbarProvider";
import type {DocumentStore} from "modules/document/state/document";
import {FluxLogger} from "modules/storage_engine/connectors/LogConnector";
import {
    bakePcbNodes,
    maybeNotifyActionBakeObserverSubscribers,
    maybeRefetchSubDocumentData,
    runtimeChecks,
    syncPatchesWithWebWorkers,
} from "modules/document/effects/sideEffects/patchHandlerSideEffects";

/**
 * Service that handles patch processing for document changes.
 *
 * This service replaces the Redux epic `makePatchHandlersEpic` using a hybrid approach:
 * it uses RxJS internally for action stream processing while fitting into the FluxServices DI pattern.
 *
 * The service subscribes to an action stream and processes patches when document state changes.
 *
 * Key behaviors:
 * - Tracks state changes to detect when patches change
 * - Notifies ActionBakeObserver subscribers for actions that are not processed
 * - Performs runtime safety checks
 * - Adds patches to undo/redo stacks for undoable actions
 * - Syncs patches with web workers (before baking)
 * - Handles PCB node rebaking
 * - Refetches sub-document data when needed
 */
export interface PatchHandlerService {
    /**
     * Called when an action is dispatched to the Redux store.
     * The service filters and processes relevant actions internally.
     */
    onAction: (action: AnyAction) => void;

    /**
     * Disposes of all subscriptions and cleans up resources.
     * Should be called when the service is no longer needed.
     */
    dispose: () => void;
}

export interface PatchHandlerServiceDependencies {
    /**
     * The Zustand app store for notifications
     */
    appStore: AppStore;
    /**
     * The full FluxServices container, needed for all the side effects
     */
    services: FluxServices;
    /**
     * The Zustand document store for reading document state.
     */
    documentStore: DocumentStore;
}

interface PatchHandlerPayload {
    action: AnyAction;
    patch: ImmerPatchSequence;
    document: IDocumentData;
}

/**
 * Creates a PatchHandlerService instance.
 *
 * This factory function creates a service that handles patch processing
 * using RxJS for action stream processing.
 *
 * @param dependencies - The service dependencies
 * @returns A PatchHandlerService instance
 */
export function createPatchHandlerService(dependencies: PatchHandlerServiceDependencies): PatchHandlerService {
    const {appStore, services, documentStore} = dependencies;
    const action$ = new Subject<AnyAction>();
    const destroy$ = new Subject<void>();
    let disposed = false;

    // Track the last seen document to detect patch changes
    // NOTE: use our own var instead of pairwise() because we want to see
    // identical states when an action has no effect on state
    let lastSeenDocument: IDocumentData | null | undefined = undefined;

    // Patch handler subscription
    // Handles processing patches for document changes
    const patchHandlerSubscription = action$
        .pipe(
            takeUntil(destroy$),
            //
            // We only want to process actions in the cases where the following criteria hold:
            // 1) the latest patch on the document has actually changed in the
            //    observed transition, and
            // 2) the latest patch is non empty
            //
            map((action) => {
                // Get document from Zustand store instead of Redux state
                const currentDocument = documentStore.getState().document;
                const lastPatch = lastSeenDocument?.latestPatch;
                lastSeenDocument = currentDocument;
                const currentPatch = currentDocument?.latestPatch;
                const currentPatchSize = currentPatch ? countPatchChanges(currentPatch) : 0;
                const shouldProcess = currentPatchSize > 0 && currentPatch?.uid !== lastPatch?.uid;
                return {
                    action,
                    shouldProcess,
                };
            }),
            //
            // Notify ActionBakeObserver subscribers if we are not processing the action
            //
            tap(maybeNotifyActionBakeObserverSubscribers(services)),
            //
            // Filter out actions that should not be processed based on above criteria
            //
            filter(({shouldProcess}) => shouldProcess),
            //
            // Extract the current data for downstream handlers
            //
            map(({action}): PatchHandlerPayload => {
                // Get document from Zustand store instead of Redux state
                const currentDocument = documentStore.getState().document;
                // TS can't tell us based on the previous filter that this condition will hold, so we need
                // to check it again.
                if (!currentDocument?.latestPatch) {
                    throw new Error(
                        `runtime logic error: latestPatch was supposed to be non-null bc of filter, but isn't. can't proceed.`,
                    );
                }

                return {action, patch: currentDocument.latestPatch, document: currentDocument};
            }),
            //
            // Runtime safety check to go with makeGenerateActionRecordsEpic
            //
            tap(runtimeChecks),
            //
            // NOTE: Undo/redo processing has been moved directly into the Zustand store's
            // reduce method (processUndoRedoInStore in document.ts). This bypasses the
            // shouldProcess check which was filtering out batch actions incorrectly.
            // The processUndoRedo tap has been removed from here as part of the full
            // Zustand migration for undo/redo.
            //
            // Patch web workers state with the latest patch.
            // NOTE: this needs to happen before baking!
            //
            tap(syncPatchesWithWebWorkers(services)),
            //
            // Handle changes that require rebaking PCB nodes
            //
            tap(bakePcbNodes(services)),
            //
            // Handle changes that require refetching sub-document data
            //
            tap(maybeRefetchSubDocumentData(services)),
            /**
             * We have to make sure we actually catch any error here, otherwise
             * the observable will shut down and we won't process any further
             * actions!
             */
            catchError((error) => {
                const message = "Error syncing data!";
                // NOTE: prepending the shared message allows easy search in Sentry
                if (error instanceof Error) {
                    error.message = message + " " + error.message;
                }
                FluxLogger.captureError(error);

                // Show sync error notification via Zustand app store if feature flag is enabled
                // This matches the original enqueueSyncErrorNotification behavior
                const showSyncErrors = services.useFeatureFlagsStore.getState().showSyncErrors ?? true;
                if (showSyncErrors) {
                    const appState = appStore.getState();
                    appState.toggleEditorBackdrop(true);
                    appState.enqueueNotification(snackbarErrorGeneratingHelper(message), "project", {
                        variant: "error",
                        preventDuplicate: true,
                        persist: true,
                        key: "syncError2",
                    });
                }
                return EMPTY;
            }),
        )
        .subscribe({
            // No actions to dispatch - the epic returns NOOP
            next: () => {
                // All side effects are handled in the tap operators above
            },
            error: (error) => {
                FluxLogger.captureError(error);
            },
        });

    const service: PatchHandlerService = {
        onAction: (action: AnyAction) => {
            if (disposed) {
                return;
            }
            action$.next(action);
        },
        dispose: () => {
            disposed = true;
            destroy$.next();
            destroy$.complete();
            patchHandlerSubscription.unsubscribe();
            action$.complete();
        },
    };

    return service;
}
